import { Circle, FabricImage, FixedLayout, Group, LayoutManager, Rect, Textbox, util } from 'fabric';
import type { Canvas, FabricObject } from 'fabric';
import { createPill } from '../../pill/utils/create-pill';
import { resizePillToFit } from '../../pill/utils/resize-pill';
import type { PillTextbox } from '../../pill/types';
import { createSobreimpreso } from '../../text/sobreimpreso/create';
import { resizeSobreimpresoHeight } from '../../text/sobreimpreso/render';
import type { SobreimpresoTextbox } from '../../text/sobreimpreso/types';
import {
  applyTextStyleRules,
  markTextStyleRulesApplied,
  refreshTextStyleRules
} from '../../text/utils/text-style-rules';
import { autoFitTextbox } from '../../text/utils/auto-fit-textbox';
import { TEXT_DEFAULTS } from '../../text/constants/text-defaults';
import { runReflow } from './reflow';
import {
  createBackgroundRect,
  ensureBackgroundConfig,
  findByPieceId,
  fitImageBackground,
  isLayerObject,
  PIECE
} from '../../canvas/utils/canvas-pieces';
import { fireModelRendered } from '../../canvas/canvas-events';
import { applyVideoControls } from '../../media/utils/video-controls';
import { applyGuard } from '../sync/apply-guard';
import { toFabricFill } from '../../shared/utils/fill';
import type { TrackMediaLoad } from '../../media/media-context';
import type { AudioLayer, Layer, LayerShadow, PieceModel } from '../types';
import type { ChromaKeyParams } from '../../shaders/constants/chroma-key-shader';
import type { AlphaFalloffParams } from '../../shaders/constants/alpha-falloff-shader';

type VisualLayer = Exclude<Layer, AudioLayer>;
type RegisterVideo = (id: string, element: HTMLVideoElement) => void;
type Effects = { chromaKey?: ChromaKeyParams; alphaFalloff?: AlphaFalloffParams };

// Applies effect flags and params from the model to the Fabric object.
const applyEffects = (obj: FabricObject, effects: Effects): void => {
  obj.chromaKeyEnabled = !!effects.chromaKey;
  if (effects.chromaKey) obj.chromaKeyParams = effects.chromaKey;
  obj.alphaFalloffEnabled = !!effects.alphaFalloff;
  if (effects.alphaFalloff) obj.alphaFalloffParams = effects.alphaFalloff;
};

const buildLayerIndex = (canvas: Canvas): Map<string, FabricObject> => {
  const map = new Map<string, FabricObject>();
  for (const obj of canvas.getObjects()) {
    if (obj.layerId) map.set(obj.layerId, obj);
  }
  return map;
};

// Creates a Fabric object for a layer that is not on the canvas yet.
const createFabricObject = (
  layer: VisualLayer,
  registerVideo?: RegisterVideo
): FabricObject | Promise<FabricObject | null> | null => {
  switch (layer.kind) {
    case 'shape': {
      const c = layer.content;
      const base = {
        originX: 'center' as const,
        originY: 'center' as const,
        fill: toFabricFill(c.fill),
        stroke: c.stroke,
        // Never pass undefined: Fabric would keep it and getBoundingRect() computes NaN.
        strokeWidth: c.strokeWidth ?? 0
      };
      return c.shape === 'circle'
        ? new Circle({ ...base, radius: c.radius })
        : new Rect({ ...base, width: c.width, height: c.height, rx: c.cornerRadius ?? 0, ry: c.cornerRadius ?? 0 });
    }

    case 'text': {
      const c = layer.content;
      const tb = new Textbox(c.text, {
        originX: 'center',
        originY: 'center',
        width: c.width,
        fontFamily: c.fontFamily,
        fontSize: c.fontSize,
        fill: toFabricFill(c.fill),
        strokeWidth: 0,
        ...(c.fontWeight !== undefined ? { fontWeight: c.fontWeight } : {}),
        ...(c.fontStyle ? { fontStyle: c.fontStyle } : {}),
        ...(c.textAlign ? { textAlign: c.textAlign } : {}),
        ...(c.lineHeight != null ? { lineHeight: c.lineHeight } : {}),
        ...(c.charSpacing != null ? { charSpacing: c.charSpacing } : {}),
        ...(c.underline ? { underline: c.underline } : {}),
        ...(c.splitByGrapheme ? { splitByGrapheme: true } : {}),
        // Clone: per-char edits mutate styles in place; keep the model immutable.
        ...(c.styles ? { styles: structuredClone(c.styles) } : {})
      });

      if (c.autoFit) tb.autoFit = { ...c.autoFit }; // clone: preview mutations must not touch the model
      if (c.wrapText) tb.wrapText = c.wrapText;
      if (c.fitToContent) tb.fitToContent = c.fitToContent;
      if (c.highlightRanges) tb.highlightRanges = c.highlightRanges;
      if (c.textStyleRules) {
        tb.textStyleRules = c.textStyleRules;
        // On mount, user-edited styles (if any) came in via c.styles above — keep them.
        // Otherwise apply the rules to seed the default look.
        if (c.styles) markTextStyleRulesApplied(tb);
        else applyTextStyleRules(tb);
      }
      return tb;
    }

    case 'pill': {
      const c = layer.content;
      const p = createPill({
        left: layer.left ?? 0,
        top: layer.top ?? 0,
        text: c.text,
        fill: c.fill,
        textFill: c.textFill,
        fontFamily: c.fontFamily,
        fontSize: c.fontSize,
        fontWeight: c.fontWeight,
        fontStyle: c.fontStyle,
        textAlign: c.textAlign,
        borderRadius: c.borderRadius,
        paddingX: c.paddingX,
        paddingY: c.paddingY,
        paddingLeft: c.paddingLeft,
        paddingRight: c.paddingRight,
        minWidth: c.minWidth,
        stroke: c.stroke,
        strokeWidth: c.strokeWidth,
        icon: c.icon
      });
      return p;
    }

    case 'sobreimpreso': {
      const c = layer.content;
      const s = createSobreimpreso({
        left: layer.left ?? 0,
        top: layer.top ?? 0,
        width: c.width,
        text: c.text,
        fill: c.fill,
        textFill: c.textFill,
        fontFamily: c.fontFamily,
        fontSize: c.fontSize,
        fontWeight: c.fontWeight,
        fontStyle: c.fontStyle,
        textAlign: c.textAlign,
        borderRadius: c.borderRadius,
        paddingX: c.paddingX,
        paddingY: c.paddingY,
        height: c.minHeight,
        stroke: c.stroke,
        strokeWidth: c.strokeWidth
      });
      if (c.styles) {
        // Clone: per-char edits mutate styles in place; keep the model immutable.
        s.set('styles', structuredClone(c.styles));
        resizeSobreimpresoHeight(s);
      }
      return s;
    }

    case 'image': {
      const c = layer.content;
      return new Promise<FabricObject>(resolve => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        const build = () => {
          const img = new FabricImage(el, {
            originX: 'center',
            originY: 'center',
            ...(c.imageFit ? { imageFit: c.imageFit } : {}),
            ...(c.imageBox ? { imageBox: c.imageBox } : {})
          });
          applyEffects(img, c);
          return img;
        };
        el.onload = () => resolve(build());
        el.onerror = () => resolve(build());
        el.src = c.src;
      });
    }

    case 'video': {
      const c = layer.content;
      if (!registerVideo) return null; // videos need the media registry to play
      return new Promise<FabricObject>(resolve => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = c.audioEnabled !== true;
        video.preload = 'auto';
        const build = () => {
          video.width = video.videoWidth;
          video.height = video.videoHeight;
          const videoId = `video_${layer.id}`;
          const obj = new FabricImage(video, { originX: 'center', originY: 'center', objectCaching: false });
          obj.set('id', videoId);
          obj.videoSrc = c.src;
          if (c.audioEnabled !== undefined) obj.audioEnabled = c.audioEnabled;
          applyEffects(obj, c);
          applyVideoControls(obj);
          registerVideo(videoId, video);
          video.currentTime = 0;
          resolve(obj);
        };
        video.addEventListener('canplaythrough', build, { once: true });
        video.addEventListener('error', build, { once: true });
        video.src = c.src;
      });
    }

    case 'group': {
      const c = layer.content;
      const kids = c.children;
      const built = kids.map(k => createFabricObject(k, registerVideo));
      return Promise.all(built.map(b => Promise.resolve(b))).then(objs => {
        const children: FabricObject[] = [];
        objs.forEach((obj, i) => {
          if (!obj) return;
          obj.layerId = kids[i].id;
          applyLayerToObject(obj, kids[i]); // children carry coords relative to the group
          // A collection restores which slots are active via Fabric's `visible` (see readGroupChildren).
          if (c.collection) obj.set('visible', kids[i].visible !== false);
          children.push(obj);
        });
        const group = new Group(children, {
          subTargetCheck: c.subTargetCheck ?? false,
          objectCaching: false,
          // 'fixed' keeps children at their absolute coords (pill-chains); default re-flows.
          ...(c.layout === 'fixed' ? { layoutManager: new LayoutManager(new FixedLayout()) } : {})
        });
        if (c.collection) group.groupCollection = c.collection;
        applyEffects(group, c);
        return group;
      });
    }

    case 'svg': {
      const c = layer.content;
      // Guard against a rejected enliven (e.g. a bad embedded src) leaving an uncaught rejection.
      return util
        .enlivenObjects([c.json])
        .then(objs => (objs[0] as FabricObject) ?? null)
        .catch(() => null);
    }

    default:
      return null;
  }
};

// Writes the model's transform, timing, animation, visibility and attachment onto a Fabric object.
const applyLayerToObject = (obj: FabricObject, layer: VisualLayer): void => {
  // Set the origin before positioning the object.
  // Fabric v6 deprecates typed originX/originY accessors, so we assign them through a plain cast.
  const withOrigin = obj as { originX?: string; originY?: string };
  withOrigin.originX = layer.originX ?? 'center';
  withOrigin.originY = layer.originY ?? 'center';
  obj.set({
    left: layer.left ?? obj.left,
    top: layer.top ?? obj.top,
    scaleX: layer.scaleX,
    scaleY: layer.scaleY,
    angle: layer.rotation,
    opacity: layer.opacity
  });

  obj.clipStart = layer.start;
  obj.clipDuration = layer.fit === 'fixed' ? layer.duration : undefined;

  if (layer.enter) {
    obj.assignedAnimation = layer.enter.preset;
    obj.animationDelay = layer.start;
    obj.animationDuration = layer.enter.duration;
  } else {
    obj.assignedAnimation = undefined;
    obj.animationDelay = undefined;
    obj.animationDuration = undefined;
  }

  obj.layerVisible = layer.visible;
  obj.selectable = !layer.locked;
  obj.attachTo = layer.attachTo;
  obj.pieceId = layer.pieceId;
  obj.laneKey = layer.laneKey;
  obj.timeAttach = layer.timeAttach;
  obj.sceneDerived = layer.sceneDerived;

  const pinned = layer.pinned === true;
  obj.lockMovementX = pinned;
  obj.lockMovementY = pinned;
  obj.lockScalingX = pinned;
  obj.lockScalingY = pinned;
  obj.lockRotation = pinned;
  obj.hasControls = !pinned;
  obj.deletable = layer.deletable !== false;

  applyShadow(obj, layer.shadow);

  obj.setCoords();
};

const applyShadow = (obj: FabricObject, shadow?: LayerShadow): void => {
  if (!shadow && !obj.shadow) return;
  obj.set({ shadow: shadow ?? null });
};

// The content object last applied to each Fabric object. Since the model is immutable, an
// unchanged `content` keeps the same reference — so we skip re-applying on moves and gestures.
const appliedContent = new WeakMap<FabricObject, VisualLayer['content']>();

const isEditing = (obj: FabricObject): boolean => (obj as { isEditing?: boolean }).isEditing === true;

// Applies a layer's content (fill, stroke, …) onto its existing Fabric object.
const applyContentToObject = (obj: FabricObject, layer: VisualLayer): void => {
  if (appliedContent.get(obj) === layer.content) return;
  appliedContent.set(obj, layer.content);

  const editing = isEditing(obj);

  switch (layer.kind) {
    case 'shape': {
      const c = layer.content;
      obj.set({ fill: toFabricFill(c.fill), stroke: c.stroke ?? null, strokeWidth: c.strokeWidth ?? 0 });
      if (c.shape === 'rect') (obj as Rect).set({ rx: c.cornerRadius ?? 0, ry: c.cornerRadius ?? 0 });
      obj.dirty = true;
      break;
    }
    case 'text': {
      const c = layer.content;
      const t = obj as Textbox;
      t.set({
        ...(editing ? {} : { text: c.text }),
        fontFamily: c.fontFamily,
        fontSize: c.fontSize,
        fill: toFabricFill(c.fill),
        // Chain textboxes have no stored width — the reflow derives it. Setting undefined here would
        // clobber the canvas width, so leave it for the reflow (like a follower's derived position).
        ...(c.width != null ? { width: c.width } : {}),
        splitByGrapheme: c.splitByGrapheme ?? false,
        fontWeight: c.fontWeight ?? TEXT_DEFAULTS.fontWeight,
        fontStyle: c.fontStyle ?? TEXT_DEFAULTS.fontStyle,
        textAlign: c.textAlign ?? TEXT_DEFAULTS.textAlign,
        lineHeight: c.lineHeight ?? TEXT_DEFAULTS.lineHeight,
        charSpacing: c.charSpacing ?? TEXT_DEFAULTS.charSpacing,
        underline: c.underline ?? TEXT_DEFAULTS.underline,
        // Only overwrite styles when the model actually carries them: clearing to {} would
        // wipe rule-derived per-char styles on rule-bearing textboxes before reverse sync
        // has captured them into the model.
        ...(c.styles ? { styles: structuredClone(c.styles) } : {})
      });
      t.highlightRanges = c.highlightRanges; // absent = no highlights
      if (c.autoFit) t.autoFit = { ...c.autoFit }; // clone: preview mutations must not touch the model
      // Rules are a text-derived default; skip re-applying while the text is unchanged
      // so a user's per-char edits (e.g. removing bold) survive. See refreshTextStyleRules.
      if (c.textStyleRules) refreshTextStyleRules(t);
      if (t.autoFit) autoFitTextbox(t, t.autoFit); // re-fit auto-fit texts to their box
      t.dirty = true;
      break;
    }
    case 'sobreimpreso': {
      const c = layer.content;
      const s = obj as SobreimpresoTextbox;
      s.set({
        ...(editing ? {} : { text: c.text }),
        fontFamily: c.fontFamily,
        fontSize: c.fontSize,
        fill: c.textFill, // the text color; the banner color lives in the meta below
        width: c.width,
        fontWeight: c.fontWeight ?? TEXT_DEFAULTS.fontWeight,
        fontStyle: c.fontStyle ?? TEXT_DEFAULTS.fontStyle,
        textAlign: c.textAlign ?? TEXT_DEFAULTS.textAlign,
        styles: c.styles ? structuredClone(c.styles) : {} // clone: per-char edits mutate in place
      });
      // Fresh meta so preview mutations on the object don't touch the (immutable) model.
      s.sobreimpreso = {
        fill: c.fill,
        stroke: c.stroke,
        strokeWidth: c.strokeWidth,
        borderRadius: c.borderRadius,
        paddingX: c.paddingX,
        paddingY: c.paddingY,
        minHeight: c.minHeight
      };
      resizeSobreimpresoHeight(s); // banner derives its height from text + padding
      break;
    }
    case 'pill': {
      const c = layer.content;
      const p = obj as PillTextbox;
      p.set({
        ...(editing ? {} : { text: c.text }),
        fontFamily: c.fontFamily,
        fontSize: c.fontSize,
        fill: c.textFill, // the text color; the pill background lives in the meta below
        fontWeight: c.fontWeight ?? TEXT_DEFAULTS.fontWeight,
        fontStyle: c.fontStyle ?? TEXT_DEFAULTS.fontStyle,
        textAlign: c.textAlign ?? TEXT_DEFAULTS.textAlign
      });
      // Fresh meta so preview mutations on the object don't touch the (immutable) model.
      p.pill = {
        fill: c.fill,
        stroke: c.stroke,
        strokeWidth: c.strokeWidth,
        borderRadius: c.borderRadius,
        paddingX: c.paddingX,
        paddingY: c.paddingY,
        paddingLeft: c.paddingLeft,
        paddingRight: c.paddingRight,
        minWidth: c.minWidth,
        icon: c.icon ? { ...c.icon } : undefined
      };
      resizePillToFit(p); // pill derives its width from text + padding + minWidth
      p.dirty = true;
      break;
    }
    case 'image': {
      const c = layer.content;
      const img = obj as FabricImage;
      if (img.getSrc() !== c.src) {
        // Source changed — reload (async). Transform-only edits skip this via the content-ref guard.
        img
          .setSrc(c.src, { crossOrigin: 'anonymous' })
          .then(() => img.canvas?.requestRenderAll())
          .catch(() => {});
      }
      img.set({ imageFit: c.imageFit, imageBox: c.imageBox });
      applyEffects(img, c);
      img.dirty = true;
      break;
    }
    case 'video': {
      const c = layer.content;
      const v = obj as FabricImage;
      if (c.audioEnabled !== undefined) v.audioEnabled = c.audioEnabled;
      applyEffects(v, c); // effect reconcilers converge chroma/alpha on modelRendered
      v.dirty = true;
      break;
    }
    case 'group':
      applyEffects(obj, layer.content);
      break;
    default:
      break; // groups are rebuilt from scratch (see restore), not patched in place
  }
};

/**
 * Syncs the editor model to the Fabric canvas.
 * Creates missing canvas objects and updates existing ones by layer ID.
 */
const placeInModelOrder = (canvas: Canvas, model: PieceModel, obj: FabricObject): void => {
  const target = obj.layerId ? model.layerOrder.indexOf(obj.layerId) : -1;
  if (target < 0) return;
  const below = canvas.getObjects().filter(o => {
    if (o === obj) return false;
    const oi = o.layerId ? model.layerOrder.indexOf(o.layerId) : -1;
    return oi < target; // non-modelled objects (background) count as below
  }).length;
  canvas.moveObjectTo(obj, below);
};

// The src being loaded, so a model change mid-load doesn't add a second background.
const loadingBackground = new WeakMap<Canvas, string>();

const addBackgroundImage = (canvas: Canvas, model: PieceModel, src: string, track?: TrackMediaLoad): void => {
  if (loadingBackground.get(canvas) === src) return;
  loadingBackground.set(canvas, src);

  applyGuard.begin();
  const pending = FabricImage.fromURL(src, { crossOrigin: 'anonymous' })
    .then(img => {
      if (loadingBackground.get(canvas) !== src) return; // the model moved on while we loaded
      loadingBackground.delete(canvas);
      img.pieceId = PIECE.BACKGROUND;
      ensureBackgroundConfig(img);
      canvas.add(img);
      canvas.sendObjectToBack(img);
      fitImageBackground(img, model.width, model.height);
      canvas.requestRenderAll();
    })
    .catch(() => loadingBackground.delete(canvas))
    .finally(() => applyGuard.end());
  track?.(pending);
};

const applyBackground = (canvas: Canvas, model: PieceModel, track?: TrackMediaLoad): void => {
  const bg = model.background;
  if (!bg) return;

  let existing = findByPieceId(canvas, PIECE.BACKGROUND);
  if (!existing) {
    // A saved piece rebuilds from the model alone, so both kinds have to be creatable.
    if (bg.kind === 'image') return addBackgroundImage(canvas, model, bg.src, track);
    existing = createBackgroundRect(model.width, model.height, bg.color);
    canvas.add(existing);
    canvas.sendObjectToBack(existing);
  }

  ensureBackgroundConfig(existing);

  if (bg.kind === 'color') {
    // Runs on every model change; only repaint when something actually moved.
    if (existing.fill === bg.color && existing.width === model.width && existing.height === model.height) return;
    existing.set({ fill: bg.color, width: model.width, height: model.height, left: 0, top: 0 });
    existing.dirty = true;
    return;
  }

  const img = existing as FabricImage;
  if (img.getSrc() === bg.src) return;
  img
    .setSrc(bg.src, { crossOrigin: 'anonymous' })
    .then(() => {
      fitImageBackground(img, model.width, model.height);
      canvas.requestRenderAll();
    })
    .catch(() => {});
};

export const applyModelToFabric = (
  canvas: Canvas,
  model: PieceModel,
  registerVideo?: RegisterVideo,
  track?: TrackMediaLoad
): void => {
  const index = buildLayerIndex(canvas);

  applyBackground(canvas, model, track);

  // Remove canvas objects that no longer exist in the model.
  const stale = canvas.getObjects().filter(obj => obj.layerId && isLayerObject(obj) && !model.layers[obj.layerId]);
  stale.forEach(obj => canvas.remove(obj));

  for (const id of model.layerOrder) {
    const layer = model.layers[id];

    if (!layer || layer.kind === 'audio') continue; // Audio layers are not rendered on the canvas.

    let obj = index.get(id);
    if (!obj) {
      const created = createFabricObject(layer, registerVideo);

      if (!created) continue; // Skip layer types that cannot be rebuilt yet.

      if (created instanceof Promise) {
        // Images, videos and groups load asynchronously. The guard stays up for the whole trip:
        // until the layer lands, the canvas is not the whole model and must not be read back.
        applyGuard.begin();
        const pending = created
          .then(loaded => {
            if (!loaded) return; // reconstruction failed (e.g. enliven error)
            loaded.layerId = layer.id;
            canvas.add(loaded);
            appliedContent.set(loaded, layer.content); // just built with this content
            placeInModelOrder(canvas, model, loaded);
            applyLayerToObject(loaded, layer);
            runReflow(canvas);
            canvas.requestRenderAll();
            fireModelRendered(canvas);
          })
          .finally(() => applyGuard.end());
        track?.(pending);
        continue;
      }
      created.layerId = layer.id;
      canvas.add(created);
      appliedContent.set(created, layer.content); // just built with this content
      obj = created;
    } else {
      applyContentToObject(obj, layer);
    }

    applyLayerToObject(obj, layer);
    placeInModelOrder(canvas, model, obj); // reused objects keep their old stacking otherwise
  }

  runReflow(canvas);
  canvas.requestRenderAll();

  // Notify effect handlers after the canvas has been updated.
  fireModelRendered(canvas);
};
